import axios from "axios";
import type { AnalysisCategory } from "@deep-consulting-solutions/observation-ui";
import type {
  ObservationSeverity,
  ObservationStatus,
} from "../utils/observationStatus";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/**
 * Columns the admin list endpoint can sort on. Kept in step with
 * `OBSERVATION_SORT_COLUMNS` in `backend/src/reusables/observationStatuses.ts`;
 * anything outside this set is rejected by the API with a 400.
 */
export const OBSERVATION_SORT_FIELDS = [
  "createdAt",
  "displayNumber",
  "reporterName",
  "title",
  "status",
  "severity",
  "processingStatus",
] as const;

export type ObservationSortField = (typeof OBSERVATION_SORT_FIELDS)[number];
export type ObservationSortOrder = "asc" | "desc";

export function isObservationSortField(
  value: string | null | undefined
): value is ObservationSortField {
  return (
    !!value && (OBSERVATION_SORT_FIELDS as readonly string[]).includes(value)
  );
}

export interface ObservationSummary {
  id: string;
  reporterId: string;
  reporterName: string;
  reporterEmail: string;
  parentObservationId: string | null;
  segmentCount: number;
  status: ObservationStatus;
  severity: ObservationSeverity | null;
  processingStatus:
    | "NEW"
    | "PREPARING"
    | "ANALYZING"
    | "COMPLETED"
    | "FAILED"
    | null;
  title: string | null;
  summary: string | null;
  displayCode: string | null;
  aiCategory: string | null;
  aiSeverity: string | null;
  createdAt: string;
}

export interface ObservationListResult {
  observations: ObservationSummary[];
  total: number;
  page: number;
  limit: number;
  offset: number;
}

export interface ObservationListParams {
  page: number;
  limit: number;
  search?: string;
  status?: ObservationStatus;
  severity?: ObservationSeverity;
  fromDate?: string;
  toDate?: string;
  sortBy: ObservationSortField;
  sortOrder: ObservationSortOrder;
}

export interface ObservationUpdatePayload {
  status?: ObservationStatus;
  severity?: ObservationSeverity | null;
  category?: AnalysisCategory | null;
  title?: string;
  summary?: string;
}

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Turns an API failure into a message worth showing someone.
 *
 * The backend already sends a specific reason for a rejected request (which
 * status values are legal, which sort field is unknown); that is far more useful
 * than "Request failed with status code 400", so it is preferred when present.
 * Anything the server did not explain is classified by status code rather than by
 * matching on message text.
 */
function toUserFacingError(error: unknown, fallback: string): Error {
  if (!axios.isAxiosError(error)) {
    return new Error(fallback);
  }

  const serverMessage = (
    error.response?.data as { message?: string; error?: string } | undefined
  )?.message;

  if (!error.response) {
    return new Error(
      "Couldn't reach the server. Check your connection and try again."
    );
  }

  switch (error.response.status) {
    case 400:
      return new Error(
        serverMessage ?? "Some of the values sent weren't valid."
      );
    case 403:
      return new Error("You don't have permission to do that.");
    case 404:
      return new Error("That observation no longer exists.");
    default:
      return new Error(serverMessage ?? fallback);
  }
}

function buildListQuery(params: ObservationListParams) {
  const query: Record<string, string> = {
    page: String(params.page),
    limit: String(params.limit),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };
  if (params.search) query.search = params.search;
  if (params.status) query.status = params.status;
  if (params.severity) query.severity = params.severity;
  if (params.fromDate) query.fromDate = params.fromDate;
  if (params.toDate) query.toDate = params.toDate;
  return query;
}

export const observationsService = {
  async list(params: ObservationListParams): Promise<ObservationListResult> {
    try {
      const response = await axios.get<{
        success: boolean;
        data: ObservationListResult;
      }>(`${API_BASE_URL}/api/observations`, {
        params: buildListQuery(params),
        headers: getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw toUserFacingError(error, "Couldn't load observations.");
    }
  },

  async updateStatus(id: string, status: ObservationStatus): Promise<void> {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/observations/${id}/status`,
        { status },
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      throw toUserFacingError(error, "Couldn't update the status.");
    }
  },

  async update(id: string, payload: ObservationUpdatePayload): Promise<void> {
    try {
      await axios.patch(`${API_BASE_URL}/api/observations/${id}`, payload, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
    } catch (error) {
      throw toUserFacingError(error, "Couldn't save your changes.");
    }
  },

  async backfill(): Promise<number> {
    try {
      const response = await axios.post<{
        success: boolean;
        data: { updatedCount: number };
      }>(
        `${API_BASE_URL}/api/observations/admin/backfill`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data?.data?.updatedCount ?? 0;
    } catch (error) {
      throw toUserFacingError(error, "Couldn't start the AI analysis run.");
    }
  },
};

export default observationsService;
